import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Droplets, Flame, Gauge, Clock, ShieldCheck } from 'lucide-react';

const LOGO_URL = "/dnb_final_header_logo_transparent.png";
const PHONE = "403-615-8770";
const EMAIL = "Radinovicw@shaw.ca";

const services = [
  { name: 'Plumbing', path: '/plumbing', icon: Droplets },
  { name: 'Heating', path: '/heating', icon: Flame },
  { name: 'Gas Fitting', path: '/gas-fitting', icon: Gauge },
];

const popularServices = [
  { name: 'Water Heater Replacement', path: '/plumbing/water-heater-replacement-calgary' },
  { name: 'Boiler & Hydronic Systems', path: '/heating/boiler-hydronic-calgary' },
  { name: 'In-Floor Radiant Heating', path: '/heating/in-floor-radiant-heating-calgary' },
  { name: 'Furnace Repair', path: '/heating/furnace-installation-repair-calgary' },
  { name: 'Drain Cleaning', path: '/plumbing/drain-cleaning-calgary' },
  { name: 'BBQ Gas Lines', path: '/gas-fitting/bbq-gas-lines-calgary' },
  { name: 'Poly-B Replacement', path: '/plumbing/poly-b-replacement-calgary' },
  { name: 'Backflow Testing', path: '/plumbing/backflow-testing-calgary' },
];

const quickLinks = [
  { name: 'About Us', path: '/about' },
  { name: 'Expert Tips', path: '/blog' },
  { name: 'Contact', path: '/contact' },
  { name: 'Privacy Policy', path: '/privacy-policy' },
];

const serviceAreas = ['Calgary', 'Airdrie', 'Chestermere', 'Cochrane', 'Okotoks', 'High River', 'Rocky View County'];

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="main-footer" className="bg-[#0F172A] text-white relative">
      {/* Main Footer */}
      <div className="py-16 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info */}
            <div>
              <Link to="/" className="inline-block mb-5 bg-white rounded-xl p-3">
                <img
                  src={LOGO_URL}
                  alt="DNB Plumbing, Heating & Gas Fitting Inc."
                  className="h-16 w-auto object-contain"
                />
              </Link>
              <p className="text-slate-300/90 text-sm mb-3 leading-relaxed">
                Calgary&rsquo;s trusted local contractor for plumbing, heating, and gas fitting. Owner-operated and proudly serving homeowners and businesses since 2009.
              </p>
              <p className="text-slate-400 text-sm font-semibold mb-6">
                Residential &middot; Light Commercial &middot; Service &amp; Renovation
              </p>
              <div className="space-y-3">
                <a
                  href={`tel:${PHONE}`}
                  data-testid="footer-phone"
                  className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors"
                >
                  <span className="flex items-center justify-center w-9 h-9 rounded-full bg-slate-500/20 text-slate-300">
                    <Phone className="w-4 h-4" />
                  </span>
                  <span className="font-semibold">{PHONE}</span>
                </a>
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="footer-email"
                  className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors break-all"
                >
                  <span className="flex items-center justify-center w-9 h-9 rounded-full bg-slate-500/20 text-slate-300 flex-shrink-0">
                    <Mail className="w-4 h-4" />
                  </span>
                  <span>{EMAIL}</span>
                </a>
                <div className="flex items-center gap-3 text-slate-300/90">
                  <span className="flex items-center justify-center w-9 h-9 rounded-full bg-slate-500/20 text-slate-300">
                    <MapPin className="w-4 h-4" />
                  </span>
                  <span>Calgary, Alberta</span>
                </div>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-heading font-bold text-white text-lg mb-6">Our Services</h4>
              <ul className="space-y-3">
                {services.map((service) => (
                  <li key={service.name}>
                    <Link
                      to={service.path}
                      data-testid={`footer-service-${service.name.toLowerCase().replace(/ /g, '-')}`}
                      className="flex items-center gap-2 text-slate-300/90 hover:text-white transition-colors"
                    >
                      <service.icon className="w-4 h-4 text-slate-400" />
                      <span>{service.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>

              <h4 className="font-heading font-bold text-white text-lg mt-8 mb-4">Popular Services</h4>
              <ul className="space-y-2">
                {popularServices.map((service) => (
                  <li key={service.name}>
                    <Link
                      to={service.path}
                      className="text-slate-300/80 hover:text-white transition-colors text-sm"
                    >
                      {service.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-heading font-bold text-white text-lg mb-6">Quick Links</h4>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      data-testid={`footer-link-${link.name.toLowerCase().replace(/ /g, '-')}`}
                      className="text-slate-300/90 hover:text-white transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="mt-8 p-4 rounded-xl bg-white/5 border border-sky-400/20">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-5 h-5 text-slate-400" />
                  <span className="font-semibold text-white">Licensed &amp; Insured</span>
                </div>
                <p className="text-xs text-slate-300/80">
                  Journeyman plumber & gas fitter. Workmanship you can trust.
                </p>
              </div>
            </div>

            {/* Service Areas & Hours */}
            <div>
              <h4 className="font-heading font-bold text-white text-lg mb-6">Service Areas</h4>
              <ul className="space-y-2 mb-8">
                {serviceAreas.map((area) => (
                  <li key={area} className="text-slate-300/90 text-sm flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#38BDF8]"></span>
                    {area}
                  </li>
                ))}
              </ul>

              <h4 className="font-heading font-bold text-white text-lg mb-4">Operating Hours</h4>
              <div className="space-y-2 text-slate-300/90 text-sm">
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-[#38BDF8] mt-0.5" />
                  <div>
                    <p>Open 7 Days a Week: 9:00 AM &ndash; 5:00 PM</p>
                    <p className="text-[#38BDF8] font-semibold mt-1">24 Hour Emergency Service Available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-300/70 text-sm">
              &copy; {currentYear} DNB Plumbing, Heating &amp; Gas Fitting Inc. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm text-slate-300/70">
              <span>Owner-operated &middot; Local &middot; Calgary, AB</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
